import React from 'react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-100 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo192.png" alt="SmartResell" className="w-8 h-8 rounded-xl" />
            <span className="font-bold text-lg" style={{ color: '#0d9488' }}>SmartResell</span>
          </div>
          <a href="/" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">← Retour</a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Politique de confidentialité</h1>
        <p className="text-sm text-gray-400 mb-10">Dernière mise à jour : 2026-04-18</p>

        <section className="mb-8">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#0d9488' }}>1. Données collectées</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            Dans le cadre de l'utilisation de SmartResell, les données suivantes peuvent être collectées :
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-1 pl-2">
            <li>Informations de compte Google (nom, adresse email, photo de profil) lors de l'authentification OAuth</li>
            <li>Données saisies par l'utilisateur : articles suivis, achats, ventes, stocks, prix</li>
            <li>Adresse Gmail Vinted configurée pour la synchronisation CRM (<em>crm_forward_email</em>)</li>
            <li>Emails entrants transmis via webhook (expéditeur, destinataire, contenu, pièces jointes)</li>
            <li>Données de navigation et d'utilisation du Service (logs techniques)</li>
            <li>Informations de paiement traitées par Stripe (aucune donnée bancaire n'est stockée sur nos serveurs)</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#0d9488' }}>2. Finalités du traitement</h2>
          <p className="text-gray-700 leading-relaxed mb-3">Les données sont traitées pour :</p>
          <ul className="list-disc list-inside text-gray-700 space-y-1 pl-2">
            <li>Permettre l'accès et le bon fonctionnement du Service</li>
            <li>Gérer les alertes de prix et les notifications</li>
            <li>Alimenter les fonctionnalités CRM (achats, ventes, stocks, comptabilité)</li>
            <li>Prévenir les abus et les utilisations frauduleuses du plan gratuit</li>
            <li>Assurer la sécurité du Service</li>
            <li>Améliorer le Service</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#0d9488' }}>3. Traitement des emails</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            SmartResell peut recevoir des emails transférés par l'utilisateur via un service de redirection (ForwardEmail). Ces emails sont traités automatiquement pour extraire les informations pertinentes (bordereaux d'expédition, notifications de vente, confirmations d'achat). Le contenu de ces emails est stocké et associé au compte de l'utilisateur. L'utilisateur configure volontairement cette redirection.
          </p>
          <p className="text-gray-700 leading-relaxed">
            L'adresse Gmail Vinted configurée pour cette synchronisation est enregistrée de manière permanente dans un registre interne afin de prévenir les abus liés à la création multiple de comptes gratuits. Cet enregistrement subsiste même après suppression du compte.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#0d9488' }}>4. Plans et limitations</h2>
          <p className="text-gray-700 leading-relaxed">
            Les plans d'abonnement (Gratuit, Starter, Hunter, Business) déterminent les fonctionnalités accessibles. Le plan Gratuit est limité à 10 ventes, 10 achats et 10 articles en stock. Ces limites sont appliquées côté frontend. SmartResell se réserve le droit de modifier ces limites à tout moment.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#0d9488' }}>5. Sous-traitants</h2>
          <p className="text-gray-700 leading-relaxed mb-3">SmartResell fait appel aux sous-traitants suivants :</p>
          <ul className="list-disc list-inside text-gray-700 space-y-1 pl-2">
            <li><strong>Hetzner</strong> — hébergement des serveurs (Allemagne, UE)</li>
            <li><strong>Google Firebase</strong> — authentification OAuth</li>
            <li><strong>ForwardEmail</strong> — traitement des emails entrants</li>
            <li><strong>Stripe</strong> — traitement des paiements</li>
            <li><strong>Cloudflare</strong> — protection anti-bot (Turnstile)</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#0d9488' }}>6. Localisation des données</h2>
          <p className="text-gray-700 leading-relaxed">
            Les données sont hébergées sur des serveurs situés dans l'Union européenne (Allemagne, Hetzner). Les transferts éventuels hors UE (notamment via Google Firebase et Stripe) sont encadrés par des mécanismes appropriés conformes au RGPD (clauses contractuelles types, etc.).
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#0d9488' }}>7. Durées de conservation</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-1 pl-2">
            <li>Données de compte : conservées jusqu'à la suppression du compte</li>
            <li>Données CRM (achats, ventes, stocks) : conservées pendant la durée d'activité du compte</li>
            <li>Emails et pièces jointes : conservés jusqu'à suppression manuelle ou suppression du compte</li>
            <li>Adresse Gmail CRM (registre anti-abus) : conservée indéfiniment</li>
            <li>Logs techniques : conservés 30 jours maximum</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#0d9488' }}>8. Sécurité</h2>
          <p className="text-gray-700 leading-relaxed">
            SmartResell met en œuvre des mesures techniques et organisationnelles appropriées pour protéger les données contre tout accès non autorisé, perte ou altération : chiffrement des communications (HTTPS/TLS), authentification sécurisée via JWT, vérification HMAC des webhooks entrants, accès restreint à la base de données. Aucun système n'est toutefois infaillible ; l'utilisateur est invité à signaler tout incident à <a href="mailto:contact@smart-resell.com" className="text-teal-600 hover:underline">contact@smart-resell.com</a>.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#0d9488' }}>9. Droits RGPD</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            Conformément au Règlement Général sur la Protection des Données (RGPD), l'utilisateur dispose des droits suivants :
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-1 pl-2">
            <li><strong>Droit d'accès</strong> : obtenir une copie des données le concernant</li>
            <li><strong>Droit de rectification</strong> : corriger des données inexactes</li>
            <li><strong>Droit à l'effacement</strong> : demander la suppression de ses données</li>
            <li><strong>Droit à la portabilité</strong> : recevoir ses données dans un format structuré</li>
            <li><strong>Droit d'opposition</strong> : s'opposer à certains traitements</li>
            <li><strong>Droit à la limitation</strong> : restreindre le traitement dans certains cas</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-3">
            Pour exercer ces droits, contactez : <a href="mailto:contact@smart-resell.com" className="text-teal-600 hover:underline">contact@smart-resell.com</a>. En cas de réponse insatisfaisante, l'utilisateur peut saisir la CNIL.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#0d9488' }}>10. Cookies</h2>
          <p className="text-gray-700 leading-relaxed">
            Le Service utilise un nombre limité de cookies strictement nécessaires au fonctionnement (token d'authentification stocké en localStorage). Aucun cookie publicitaire ou de tracking tiers n'est utilisé.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#0d9488' }}>11. Contact</h2>
          <p className="text-gray-700 leading-relaxed">
            Pour toute question relative à la présente politique ou à vos données personnelles :<br />
            <a href="mailto:contact@smart-resell.com" className="text-teal-600 hover:underline">contact@smart-resell.com</a>
          </p>
        </section>

        <div className="mt-12 pt-8 border-t border-gray-100 text-center text-sm text-gray-400">
          SmartResell — <a href="mailto:contact@smart-resell.com" className="text-teal-600 hover:underline">contact@smart-resell.com</a>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
